import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { PlusIcon, SheetOutlineIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createCenterEventAction } from '../../../api/center-event/create-center-event-action'
import { getCenterEventActions } from '../../../api/center-event/get-center-event-actions'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { CenterEvent } from '../../../lib/types/models/center-event/center-event'
import { CenterEventMode } from '../../../lib/types/models/center-event/center-event-input'
import { CenterEventAction } from '../../../lib/types/models/center-event-action/center-event-action'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import CenterEventActionForm from './center-event-action-form'
import CenterEventActionItem from './center-event-action-item'

type Props = {
  centerEvent: CenterEvent
  mode?: CenterEventMode
}

const CenterEventActionLog = ({ centerEvent, mode = 'NORMAL' }: Props) => {
  const queryClient = useQueryClient()
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'view',
    CenterEventAction
  >(['create', 'view'])

  const { mutateAsync } = useMutation({
    mutationFn: createCenterEventAction,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Action ajoutée avec succès')
      queryClient.invalidateQueries({ queryKey: ['actions', { centerEventId: centerEvent.id }] })
      closeModel('create')
    }
  })

  const { data: actions } = useQuery({
    queryKey: ['actions', { centerEventId: centerEvent.id }],
    queryFn: () => getCenterEventActions(centerEvent.id)
  })

  return (
    <Box className="drop-shadow-lg flex flex-col gap-3">
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Journal des actions
      </Typography>
      {mode === 'NORMAL' && (
        <Button
          icon={PlusIcon}
          label="Ajouter une action"
          variant="secondary"
          className="w-fit"
          onClick={() => toggleModal('create')}
        />
      )}
      {mode === 'VIEW' && (!actions || actions.data.length === 0) && (
        <div className="p-3 bg-white-hover rounded-lg">
          <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
            Aucune action enregistrée
          </Typography>
        </div>
      )}

      {actions && actions.data.length > 0 && (
        <Box className="bg-white-hover rounded-lg">
          {actions?.data.map((action) => (
            <CenterEventActionItem
              mode="item"
              key={action.id}
              action={action}
              onClick={() => {
                toggleModal('view', action)
              }}
            />
          ))}
        </Box>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState.view}
          title={"Détails de l'action"}
          icon={SheetOutlineIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('view')}
        >
          <div className="w-full divide-y divide-white-active">
            <CenterEventActionItem
              mode="view"
              action={selectedData}
              className={MODAL_CONTENT_CLASSNAME}
            />
            <div className="flex justify-end p-6">
              <Button label="Fermer" variant="ghost" onClick={() => closeModel('view')} />
            </div>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={modalState.create}
        title={'Ajouter une action'}
        icon={SheetOutlineIcon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <CenterEventActionForm
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onConfirm(values) {
              return mutateAsync({ centerEventId: centerEvent.id, data: values })
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>
    </Box>
  )
}

export default CenterEventActionLog
